// Tab "Crew": wie draait er mee, met rol, contact en call-tijd; crew bewaren/laden als template.
import React, { useEffect, useState } from 'react';
import {
  Box, ButtonBase, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, InputBase, TextField, Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import toast from 'react-hot-toast';
import { bevestig } from '../../../utils/bevestig';
import { biedOngedaanAan } from '../../../utils/ongedaan';
import { meldFout } from '../../../utils/apiFout';
import {
  api, crewTemplateOpslaan, crewTemplates, crewTemplateToepassen, nieuweCrew, wijzigCrew, INKT, LIJN, STIL,
} from '../breakdownApi';
import { Kaart, Leeg, Primair, Rij, Secundair, Stil, Uitleg } from '../onderdelen';

const LEEG = { name: '', role: '', contact: '', calltime: '' };

function CallTijd({ lid, onBewaar }) {
  const [tekst, setTekst] = useState(lid.calltime || '');
  useEffect(() => { setTekst(lid.calltime || ''); }, [lid.calltime]);
  return (
    <InputBase value={tekst} onChange={(e) => setTekst(e.target.value)} placeholder="Call"
      onBlur={() => { if (tekst !== (lid.calltime || '')) onBewaar(tekst); }} inputProps={{ 'aria-label': `Call-tijd ${lid.name}` }}
      sx={{ width: 72, fontSize: 13, border: `1px solid ${LIJN}`, borderRadius: 1, px: 0.75 }} />
  );
}

export default function CrewTab({ productie, crew, setCrew }) {
  const [form, setForm] = useState(LEEG);
  const [bewaarOpen, setBewaarOpen] = useState(false);
  const [templateNaam, setTemplateNaam] = useState('');
  const [laadOpen, setLaadOpen] = useState(false);
  const [templates, setTemplates] = useState(null);

  const vervang = (c) => setCrew((lijst) => lijst.map((x) => (x.id === c.id ? c : x)));

  async function voegToe() {
    if (!form.name.trim()) return;
    try {
      setCrew([...crew, await nieuweCrew(productie.id, form)]);
      setForm(LEEG);
    } catch (e) { meldFout(e, 'Crew toevoegen'); }
  }

  async function bevestigCall(c) {
    const vorige = c.is_call_confirmed;
    try {
      vervang(await wijzigCrew(c.id, { is_call_confirmed: !vorige }));
      biedOngedaanAan(`Call ${c.name} ${vorige ? 'weer open' : 'bevestigd'}`, async () => vervang(await wijzigCrew(c.id, { is_call_confirmed: vorige })));
    } catch (e) { meldFout(e, 'Crew opslaan'); }
  }

  async function verwijder(c) {
    const ok = await bevestig({ titel: `${c.name} verwijderen?`, tekst: 'Dit crewlid gaat van deze productie af.', knop: 'Verwijderen', gevaarlijk: true });
    if (!ok) return;
    try {
      await api(`/crew/${c.id}`, { method: 'DELETE', wat: 'Crew verwijderen' });
      setCrew((lijst) => lijst.filter((x) => x.id !== c.id));
    } catch (e) { meldFout(e, 'Crew verwijderen'); }
  }

  async function bewaarTemplate() {
    if (!templateNaam.trim()) return;
    try {
      await crewTemplateOpslaan(templateNaam.trim(), crew.map((c) => ({ name: c.name, role: c.role })));
      toast.success(`Template "${templateNaam.trim()}" bewaard`);
      setBewaarOpen(false);
      setTemplateNaam('');
    } catch (e) { meldFout(e, 'Template bewaren'); }
  }

  async function openLaden() {
    setLaadOpen(true);
    try { setTemplates(await crewTemplates()); } catch (e) { meldFout(e, 'Templates laden'); setTemplates([]); }
  }

  async function pasToe(t) {
    try {
      const nieuw = await crewTemplateToepassen(productie.id, t.id);
      setCrew([...crew, ...nieuw]);
      setLaadOpen(false);
      biedOngedaanAan(`${nieuw.length} crewleden uit "${t.name}" toegevoegd`, async () => {
        await Promise.all(nieuw.map((c) => api(`/crew/${c.id}`, { method: 'DELETE', wat: 'Crew verwijderen' })));
        const weg = new Set(nieuw.map((c) => c.id));
        setCrew((lijst) => lijst.filter((x) => !weg.has(x.id)));
      });
    } catch (e) { meldFout(e, 'Template toepassen'); }
  }

  async function verwijderTemplate(t) {
    const ok = await bevestig({ titel: `Template "${t.name}" verwijderen?`, tekst: 'De template verdwijnt voor iedereen. Crew die er al mee is toegevoegd blijft staan.', knop: 'Verwijderen', gevaarlijk: true });
    if (!ok) return;
    try {
      await api(`/crew-templates/${t.id}`, { method: 'DELETE', wat: 'Template verwijderen' });
      setTemplates((lijst) => lijst.filter((x) => x.id !== t.id));
    } catch (e) { meldFout(e, 'Template verwijderen'); }
  }

  const bevestigd = crew.filter((c) => c.is_call_confirmed).length;
  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
        <Uitleg>{crew.length ? `${bevestigd} van ${crew.length} calls bevestigd.` : 'Nog geen crew op deze productie.'}</Uitleg>
        <Box sx={{ flex: 1 }} />
        <Secundair onClick={openLaden}>Uit template…</Secundair>
        {crew.length > 0 && <Secundair onClick={() => setBewaarOpen(true)}>Bewaar als template</Secundair>}
      </Box>

      {crew.length === 0 ? <Leeg>Voeg hieronder crew toe, of laad een vaste ploeg uit een template.</Leeg> : (
        <Kaart>
          {crew.map((c) => (
            <Rij key={c.id}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography noWrap sx={{ fontSize: 14, fontWeight: 600, color: INKT }}>{c.name}</Typography>
                <Typography noWrap sx={{ fontSize: 12.5, color: STIL }}>{[c.role, c.contact].filter(Boolean).join(' · ') || '—'}</Typography>
              </Box>
              <CallTijd lid={c} onBewaar={async (t) => { try { vervang(await wijzigCrew(c.id, { calltime: t })); } catch (e) { meldFout(e, 'Crew opslaan'); } }} />
              <ButtonBase onClick={() => bevestigCall(c)} aria-pressed={c.is_call_confirmed}
                sx={{ px: 1, py: 0.3, borderRadius: 10, fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap',
                  color: c.is_call_confirmed ? '#15803d' : STIL, bgcolor: c.is_call_confirmed ? '#dcfce7' : '#f1f3f6' }}>
                {c.is_call_confirmed ? 'Call bevestigd' : 'Call open'}
              </ButtonBase>
              <IconButton size="small" aria-label={`${c.name} verwijderen`} onClick={() => verwijder(c)}><DeleteOutlineIcon fontSize="small" /></IconButton>
            </Rij>
          ))}
        </Kaart>
      )}

      <Kaart sx={{ mt: 2, p: 1.5, display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1.2fr 1fr 1.2fr 90px auto' }, gap: 1, alignItems: 'center' }}>
        <TextField size="small" label="Naam" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
          onKeyDown={(e) => { if (e.key === 'Enter') voegToe(); }} />
        <TextField size="small" label="Rol" value={form.role} onChange={(e) => setForm((f) => ({ ...f, role: e.target.value }))} />
        <TextField size="small" label="Telefoon / e-mail" value={form.contact} onChange={(e) => setForm((f) => ({ ...f, contact: e.target.value }))} />
        <TextField size="small" label="Call" value={form.calltime} onChange={(e) => setForm((f) => ({ ...f, calltime: e.target.value }))} />
        <Primair onClick={voegToe} disabled={!form.name.trim()}>Toevoegen</Primair>
      </Kaart>

      <Dialog open={bewaarOpen} onClose={() => setBewaarOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Crew bewaren als template</DialogTitle>
        <DialogContent sx={{ pt: '8px !important' }}>
          <TextField fullWidth size="small" autoFocus label="Naam" placeholder="bv. Klein commercialteam" value={templateNaam}
            onChange={(e) => setTemplateNaam(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') bewaarTemplate(); }} />
          <Typography sx={{ mt: 1, fontSize: 12.5, color: STIL }}>Namen en rollen van {crew.length} crewleden; contact en call-tijden niet.</Typography>
        </DialogContent>
        <DialogActions>
          <Stil onClick={() => setBewaarOpen(false)}>Annuleren</Stil>
          <Primair onClick={bewaarTemplate} disabled={!templateNaam.trim()}>Bewaren</Primair>
        </DialogActions>
      </Dialog>

      <Dialog open={laadOpen} onClose={() => setLaadOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Crew uit template</DialogTitle>
        <DialogContent>
          {templates === null ? null : templates.length === 0 ? (
            <Typography sx={{ fontSize: 13.5, color: STIL }}>Nog geen templates. Bewaar eerst de crew van een productie als template.</Typography>
          ) : templates.map((t) => (
            <Box key={t.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, border: `1px solid ${LIJN}`, borderRadius: 1.5, px: 1.25, py: 0.75, mb: 1 }}>
              <ButtonBase onClick={() => pasToe(t)} sx={{ flex: 1, display: 'block', textAlign: 'left' }}>
                <Typography sx={{ fontSize: 14, fontWeight: 600 }}>{t.name}</Typography>
                <Typography noWrap sx={{ fontSize: 12.5, color: STIL }}>{t.members.map((m) => m.role ? `${m.name} (${m.role})` : m.name).join(', ')}</Typography>
              </ButtonBase>
              <IconButton size="small" aria-label={`Template ${t.name} verwijderen`} onClick={() => verwijderTemplate(t)}><DeleteOutlineIcon fontSize="small" /></IconButton>
            </Box>
          ))}
        </DialogContent>
        <DialogActions><Stil onClick={() => setLaadOpen(false)}>Sluiten</Stil></DialogActions>
      </Dialog>
    </Box>
  );
}
